<template>
  <Teleport to="body">
    <Transition name="lightbox-fade">
      <div v-if="show" class="image-lightbox" @click.self="close">
        <button class="image-lightbox__close" @click="close" aria-label="Close">
          <Icon icon="mdi:close" />
        </button>
        <button
          v-if="hasMultipleImages"
          class="image-lightbox__nav image-lightbox__nav--prev"
          @click="previousImage"
          aria-label="Previous image"
        >
          <Icon icon="mdi:chevron-left" />
        </button>
        <button
          v-if="hasMultipleImages"
          class="image-lightbox__nav image-lightbox__nav--next"
          @click="nextImage"
          aria-label="Next image"
        >
          <Icon icon="mdi:chevron-right" />
        </button>
        <div class="image-lightbox__container">
          <img
            :src="currentImageUrl"
            :alt="currentImageAlt"
            class="image-lightbox__image"
            @error="handleImageError"
          />
          <div v-if="isLoading" class="image-lightbox__loading">
            <Icon icon="mdi:loading" class="image-lightbox__loading-spinner" />
            <span>Loading image...</span>
          </div>
          <div v-if="imageError" class="image-lightbox__error">
            <Icon icon="mdi:image-off" />
            <span>Failed to load image</span>
          </div>
        </div>
        <div v-if="hasMultipleImages" class="image-lightbox__counter">
          {{ currentIndex + 1 }} / {{ images.length }}
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import baseAPI from "@/api/base";

interface Props {
  show: boolean;
  images: string[];
  currentIndex?: number;
}

const props = withDefaults(defineProps<Props>(), {
  currentIndex: 0,
});

const emit = defineEmits<{
  close: [];
  "update:currentIndex": [index: number];
}>();

const currentIndex = ref(props.currentIndex);
const isLoading = ref(false);
const imageError = ref(false);
const imageBlobUrls = ref<Map<string, string>>(new Map());

const hasMultipleImages = computed(() => props.images.length > 1);

const isApiUrl = (url: string): boolean => {
  return url.startsWith("/unipile/") || url.startsWith("/api/");
};

const loadImageAsBlob = async (url: string): Promise<string> => {
  if (imageBlobUrls.value.has(url)) {
    return imageBlobUrls.value.get(url)!;
  }

  if (isApiUrl(url)) {
    try {
      const response = await baseAPI.get(url, { responseType: "blob" });
      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);
      imageBlobUrls.value.set(url, blobUrl);
      return blobUrl;
    } catch (error) {
      console.error("Failed to load image from API:", error);
      throw error;
    }
  }

  return url;
};

const currentImageUrl = computed(() => {
  if (props.images.length === 0) return "";
  const url = props.images[currentIndex.value] || props.images[0];
  return imageBlobUrls.value.get(url) || url;
});

const currentImageAlt = computed(() => `Image ${currentIndex.value + 1} of ${props.images.length}`);

const close = () => {
  emit("close");
};

const previousImage = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  } else if (hasMultipleImages.value) {
    currentIndex.value = props.images.length - 1;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  }
};

const nextImage = () => {
  if (currentIndex.value < props.images.length - 1) {
    currentIndex.value++;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  } else if (hasMultipleImages.value) {
    currentIndex.value = 0;
    emit("update:currentIndex", currentIndex.value);
    imageError.value = false;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.show) return;

  switch (event.key) {
    case "Escape":
      close();
      break;
    case "ArrowLeft":
      previousImage();
      break;
    case "ArrowRight":
      nextImage();
      break;
  }
};

const handleImageError = () => {
  imageError.value = true;
  isLoading.value = false;
};

watch(
  () => props.currentIndex,
  (newIndex) => {
    currentIndex.value = newIndex;
    imageError.value = false;
  }
);

const loadCurrentImage = async () => {
  if (props.images.length === 0) return;

  const url = props.images[currentIndex.value] || props.images[0];
  imageError.value = false;
  isLoading.value = true;

  try {
    let imageUrl = url;

    if (isApiUrl(url)) {
      imageUrl = await loadImageAsBlob(url);
    }

    const img = new Image();
    img.onload = () => {
      isLoading.value = false;
    };
    img.onerror = () => {
      isLoading.value = false;
      imageError.value = true;
    };
    img.src = imageUrl;
  } catch (error) {
    isLoading.value = false;
    imageError.value = true;
  }
};

watch(
  () => currentIndex.value,
  () => {
    if (props.show) {
      loadCurrentImage();
    }
  }
);

watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      loadCurrentImage();
    }
  }
);

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  imageBlobUrls.value.forEach((blobUrl) => {
    window.URL.revokeObjectURL(blobUrl);
  });
  imageBlobUrls.value.clear();
});
</script>

<style scoped>
.image-lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.image-lightbox__container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-lightbox__image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  cursor: default;
}

.image-lightbox__close {
  position: fixed;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  transition: background 0.2s;
  z-index: 10000;
}

.image-lightbox__close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.image-lightbox__nav {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.5rem;
  transition: background 0.2s;
  z-index: 10000;
}

.image-lightbox__nav:hover {
  background: rgba(255, 255, 255, 0.2);
}

.image-lightbox__nav--prev {
  left: 1rem;
}

.image-lightbox__nav--next {
  right: 1rem;
}

.image-lightbox__counter {
  position: fixed;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  z-index: 10000;
}

.image-lightbox__loading,
.image-lightbox__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  color: white;
  font-size: 1rem;
  padding: 2rem;
}

.image-lightbox__loading-spinner {
  font-size: 2rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.3s;
}

.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
